import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { VersionNotification } from '@/types/version';
import { versionApi } from '@/lib/axios/versionApi';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const VERSION_HUB_URL = `${API_BASE_URL.replace('/api', '')}/versionHub`;

interface VersionContextType {
  versionNotification: VersionNotification | null;
  dismissNotification: () => void;
  isConnected: boolean;
  fetchLatestVersionAndApply: () => Promise<void>;
}

const APP_VERSION_KEY = 'appVersion';

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export function VersionProvider({ children }: { children: ReactNode }) {
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [versionNotification, setVersionNotification] = useState<VersionNotification | null>(null);
  const lastHiddenAtRef = useRef<number | null>(null);
  const SUSPEND_FORCE_RESTART_MS = 60000; // 60s
  const connectInProgressRef = useRef<boolean>(false);
  const restartInProgressRef = useRef<boolean>(false);
  const connectPromiseRef = useRef<Promise<void> | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | null>(() => {
    try {
      return localStorage.getItem(APP_VERSION_KEY);
    } catch {
      return null;
    }
  });

  const dismissNotification = useCallback(() => {
    setVersionNotification(null);
  }, []);

  const applyNewVersionNotification = useCallback((notification: VersionNotification, isReload = false) => {
    const storedVersion = currentVersion;

    // If already on this version, do nothing (prevents reload loop even when forceUpdate=true)
    if (storedVersion && storedVersion === notification.version) {
      return;
    }

    // Initialize baseline version if none exists yet
    if (!storedVersion) {
      try {
        localStorage.setItem(APP_VERSION_KEY, notification.version);
        setCurrentVersion(notification.version);
      } catch {
        // ignore storage errors
      }
      // For force update on first install: just save version, don't reload
      // The app is already running the latest version, no need to reload
      return;
    }

    setVersionNotification(notification);

    if (notification.isForceUpdate) {
      try {
        localStorage.setItem(APP_VERSION_KEY, notification.version);
        setCurrentVersion(notification.version);
      } catch {
        // ignore storage errors
      }

      setTimeout(() => {
        // Only reload if isReload is true
        if (!isReload) return; 
        window.dispatchEvent(new Event('dashboardChanged'));
        setVersionNotification(null);
        console.log('[VersionContext] Reloading tab: force update to version', notification.version, { isForceUpdate: notification.isForceUpdate });
        window.location.reload();
      }, 2000);
    }
  }, [currentVersion]);

  const fetchLatestVersionAndApply = useCallback(async () => {
    try {
      const data = await versionApi.getLatest();
      if (!data) return;
      applyNewVersionNotification({
        version: data.version,
        description: data.description,
        isForceUpdate: data.isForceUpdate,
        createdAt: data.createdAt,
      });
    } catch (error) {
      // Silently ignore fetch errors to avoid blocking UI
      console.warn('Version check failed', error);
    }
  }, [applyNewVersionNotification]);

  const connectToVersionHub = useCallback(async () => {
    // Prevent overlapping connection attempts
    if (connectInProgressRef.current) {
      return connectPromiseRef.current ?? Promise.resolve();
    }

    // If an existing connection is already connected/connecting, skip
    const state = hubConnection?.state;
    if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting) {
      return;
    }

    connectInProgressRef.current = true;
    const doConnect = (async () => {
      try {
      const connection = new HubConnectionBuilder()
        .withUrl(VERSION_HUB_URL)
        .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
        .build();

      // Heartbeat/timeout to detect half-open after resume
      connection.keepAliveIntervalInMilliseconds = 15000;
      connection.serverTimeoutInMilliseconds = 45000;

      connection.on('NewVersionAvailable', (notification: VersionNotification) => {
        applyNewVersionNotification(notification, true);
      });

      connection.onclose(() => {
        console.log('Version Hub: Connection closed');
        setIsConnected(false);
      });

      connection.onreconnecting(() => {
        console.log('Version Hub: Reconnecting...');
      });

      connection.onreconnected(() => {
        console.log('Version Hub: Reconnected');
        setIsConnected(true);
        // After reconnection, proactively check latest version via REST
        fetchLatestVersionAndApply();
      });

        await connection.start();
        console.log('Version Hub: Connected');
        setIsConnected(true);
        setHubConnection(connection);
        // On initial connect (F5), also check latest version via REST
        fetchLatestVersionAndApply();
      } catch (error) {
        console.error('Version Hub: Failed to connect', error);
      } finally {
        connectInProgressRef.current = false;
        connectPromiseRef.current = null;
      }
    })();
    connectPromiseRef.current = doConnect;
    return doConnect;
  }, [hubConnection, fetchLatestVersionAndApply, applyNewVersionNotification]);

  useEffect(() => {
    connectToVersionHub();
  }, [connectToVersionHub]);

  useEffect(() => {
    return () => {
      if (hubConnection) {
        hubConnection.stop();
      }
    };
  }, [hubConnection]);

  // Handle visibility/network changes to avoid half-open state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenAtRef.current = Date.now();
        return;
      }

      const hiddenForMs = lastHiddenAtRef.current ? Date.now() - lastHiddenAtRef.current : 0;
      lastHiddenAtRef.current = null;

      if (hiddenForMs > SUSPEND_FORCE_RESTART_MS) {
        console.log(`Version Hub: Tab resumed after ${hiddenForMs}ms hidden. Restarting hub.`);
        if (restartInProgressRef.current) return;
        restartInProgressRef.current = true;
        (async () => {
          try {
            await hubConnection?.stop();
          } catch (_err) {
            console.warn('Version Hub: stop() during resume failed', _err);
          }
          setHubConnection(null);
          setIsConnected(false);
          await connectToVersionHub();
          restartInProgressRef.current = false;
        })();
        return;
      }

      // Ensure connection if silently dropped
      const state = hubConnection?.state;
      if (!isConnected || state !== HubConnectionState.Connected) {
        connectToVersionHub();
      }
    };

    const handleOnline = () => {
      const state = hubConnection?.state;
      if (!isConnected || state !== HubConnectionState.Connected) {
        console.log('Version Hub: Browser online, ensuring connection...');
        connectToVersionHub();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [hubConnection, isConnected, connectToVersionHub]);

  return (
    <VersionContext.Provider value={{ versionNotification, dismissNotification, isConnected, fetchLatestVersionAndApply }}>
      {children}
    </VersionContext.Provider>
  );
}

export function useVersion() {
  const context = useContext(VersionContext);
  if (context === undefined) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  return context;
}

